import {
  FontPair,
  isFontPair,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/font-config';
import {
  isPalette,
  MainPalette,
  Palette,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/palette-config';
import {
  FontPairs as LegacyFontPairs,
  Palettes as LegacyPalettes,
} from '@bridebook/web/lib/wedding-website/types';

export const toValidPalette = (palette: string): Palette => {
  if (isPalette(palette)) return palette;
  return Palette.Bridebook;
};

export const toValidFontPair = (fontPair: string): FontPair => {
  if (isFontPair(fontPair)) return fontPair;
  return FontPair.Bridebook;
};

/**
 * Converts extended palette to main palette.
 * Used only in the context of new primary layout.
 * For example to add some "palette specific" changes to the layout. (eg different padding around some texts to not overlap with accents)
 */
export const toMainPalette = (palette: Palette): MainPalette => {
  switch (palette) {
    case Palette.Bridebook:
    case Palette.BridebookGreen:
    case Palette.BridebookBlush:
      return Palette.Bridebook;
    case Palette.Roses:
    case Palette.RosesPlum:
    case Palette.RosesBrown:
      return Palette.Roses;
    case Palette.Foliage:
    case Palette.FoliageOrange:
    case Palette.FoliageBlue:
      return Palette.Foliage;
    case Palette.Whimsical:
    case Palette.WhimsicalBeige:
    case Palette.WhimsicalGreen:
      return Palette.Whimsical;
    case Palette.Sketches:
    case Palette.SketchesGreen:
    case Palette.SketchesLilac:
      return Palette.Sketches;
    case Palette.Glamour:
    case Palette.GlamourNavy:
    case Palette.GlamourChampagne:
      return Palette.Glamour;
    case Palette.Timeless:
      return Palette.Timeless;
    case Palette.Blossom:
      return Palette.Blossom;
    case Palette.Amore:
      return Palette.Amore;
    default:
      return Palette.Bridebook;
  }
};

/* ############################################################################
 *  Legacy palette utils
 *  Used as normalizers for new palette values used in legacy layout
 * ######################################################################### */

export const isLegacyPalette = (value: string): value is LegacyPalettes =>
  Object.values(LegacyPalettes).includes(value as LegacyPalettes);

/**
 * Normalizer for palette values used in legacy layout.
 * Needed for backward compatibility with old themes for areas that are not supported in new layout yet (password, website thumbnail, etc.)
 */
export const toLegacyPalette = (palette: string): LegacyPalettes => {
  if (isLegacyPalette(palette)) return palette;
  switch (palette) {
    case Palette.Bridebook:
    case Palette.BridebookGreen:
    case Palette.BridebookBlush:
      return LegacyPalettes.Bridebook;
    case Palette.Roses:
    case Palette.RosesPlum:
    case Palette.RosesBrown:
      return LegacyPalettes.Roses;
    case Palette.Foliage:
    case Palette.FoliageOrange:
    case Palette.FoliageBlue:
      return LegacyPalettes.Foliage;
    case Palette.Whimsical:
    case Palette.WhimsicalBeige:
    case Palette.WhimsicalGreen:
      return LegacyPalettes.Whimsical;
    default:
      return LegacyPalettes.Bridebook;
  }
};

export const isLegacyFontPair = (value: string): value is LegacyFontPairs =>
  Object.values(LegacyFontPairs).includes(value as LegacyFontPairs);

/**
 * Normalizer for font pair values used in legacy layout.
 * Needed for backward compatibility with old themes for areas that are not supported in new layout yet (password, website thumbnail, etc.)
 */
export const toLegacyFontPair = (fontPair: string): LegacyFontPairs => {
  if (isLegacyFontPair(fontPair)) return fontPair;
  switch (fontPair) {
    case FontPair.Bridebook:
      return LegacyFontPairs.Bridebook;
    case FontPair.Roses:
      return LegacyFontPairs.Roses;
    case FontPair.Foliage:
      return LegacyFontPairs.Foliage;
    case FontPair.Whimsical:
      return LegacyFontPairs.Whimsical;
    default:
      return LegacyFontPairs.Bridebook;
  }
};
